import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';
import EmptyState from '../EmptyState';

/**
 * BD-18 — "for you to send": the people a colleague's thought-leadership piece is relevant to, among the contacts the
 * signed-in person owns, each with the reason. They choose who gets a draft. A pick becomes a draft proposal in
 * Approvals (then an Outlook draft they send themselves); nothing is sent and nothing is made for someone they skip.
 * Only the owner's own suggestions are ever listed.
 */

interface Target {
  id: number;
  status: 'suggested' | 'drafted';
  reasons: { text: string }[];
  piece: { id: number; title: string };
  person: { id: number; name: string; title: string | null };
  can_draft: boolean;
  draft_action_id: number | null;
}

const ThoughtLeadershipForYou: React.FC = () => {
  const [targets, setTargets] = useState<Target[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/bd/thought-leadership/my-targets');
      setTargets(res.data.targets || []);
    } catch {
      toast.error('Could not load the suggestions');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (t: Target, path: 'draft' | 'dismiss', ok: string) => {
    setBusy(t.id);
    try {
      await api.post(`/api/bd/thought-leadership/targets/${t.id}/${path}`);
      toast.success(ok);
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'That did not work');
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (targets.length === 0) {
    return <EmptyState icon={Send} title="Nothing to send right now" description="When a colleague's approved piece fits people you know, they are suggested here with the reason. You choose who gets a draft." />;
  }
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500">Each person is here because of the reason shown. Choose who gets a draft; every draft waits in Approvals and is never sent for you.</p>
      <ul className="space-y-3">
        {targets.map((t) => (
          <li key={t.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-testid={`tl-target-${t.id}`}>
            <p className="font-medium">{t.person.name}{t.person.title ? `, ${t.person.title}` : ''}</p>
            <p className="text-sm text-gray-500">For: {t.piece.title}</p>
            <ul className="mt-1 text-sm list-disc ml-5">{t.reasons.map((r, i) => <li key={i}>{r.text}</li>)}</ul>
            <div className="mt-3 flex items-center gap-2">
              {t.status === 'drafted' ? (
                <Link className="text-sm text-blue-600 hover:underline" to="/app/business-development?tab=approvals">Draft waiting in Approvals</Link>
              ) : (
                <>
                  <button type="button" disabled={busy === t.id || !t.can_draft} onClick={() => act(t, 'draft', 'Draft is waiting in Approvals')} className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white disabled:opacity-50">Make a draft</button>
                  <button type="button" disabled={busy === t.id} onClick={() => act(t, 'dismiss', 'Left out')} className="px-3 py-1.5 text-sm rounded border border-gray-300">Not for them</button>
                  {!t.can_draft && <span className="text-xs text-gray-500">The author has not approved the client email yet.</span>}
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ThoughtLeadershipForYou;
